import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  PlanOptionValue,
  setModelOptions,
  setModelValue,
  setPlanValue
} from 'common/modules/app/filter';
import { Glossary, PlanListGL } from 'common/utils/glossary';
import {
  loadCookie,
  SasCookieKeys,
  saveCookie
} from 'common/utils/cookie-utils';
import { sasStaticUrl } from 'common/utils/urls';

interface PidListType {
  [pid: string]: {
    category: string;
    mobile_apps: string[];
    name_by_region: { [regionCode: number]: string }; // {0: 'EOS Kiss M2', 1: 'EOS M50 Mark Ⅱ'}
    others: string[];
    pc_apps: string[];
  };
}

interface FilterProps {
  /** クエリパラメータの取得や操作を行うためのURLSearchParamsオブジェクト */
  query: URLSearchParams;
  /** プランのプルダウンの値 */
  planValue: PlanOptionValue;
  /** 機種のプルダウンの値 */
  modelValue: string;
  /** 機種のプルダウンの選択肢 */
  modelOptions: string[];
  /** 画面のデータ取得処理の実行状況 */
  isProcessingList: boolean;
  /** グロッサリ */
  glossary: Glossary;
  /** プランのプルダウンを変更する処理 */
  onChangePlan: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** 機種のプルダウンを変更する処理 */
  onChangeModel: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** クリアボタンの処理 */
  onClickFilterClear: () => void;
}

/** プラン一覧画面・利用中一覧画面の絞り込みのPresentation Component */
export const Filter: React.FC<FilterProps> = ({
  query,
  planValue,
  modelValue,
  modelOptions,
  isProcessingList,
  glossary,
  onChangePlan,
  onChangeModel,
  onClickFilterClear
}) => {
  const dispatch = useDispatch();
  /** リージョンID */
  const [regionCode, setRegionCode] = useState(0);
  /** models.jsonから取得したデータ */
  const [pIdModelNameList, setPIdModelNameList] = useState<PidListType>({});

  /** 初期表示時の処理 */
  useEffect(() => {
    // 現在指定されているURLのクエリパラメータを取得してcookieに保持する
    const nowFilter = query.get('model') || '';
    const nowTypeFilter = query.get('type') || '';
    if (nowFilter && nowTypeFilter) {
      // modelとtypeの両方指定されていたらmodelを優先して保存
      saveCookie(SasCookieKeys.KEY_MODEL_NAME, nowFilter);
    } else if (nowFilter) {
      // modelが指定されていたらmodelNameを保存
      saveCookie(SasCookieKeys.KEY_MODEL_NAME, nowFilter);
    } else if (nowTypeFilter) {
      // typeが指定されていたらtypeを保存
      saveCookie(SasCookieKeys.KEY_TYPE, nowTypeFilter);
    } else {
      // 何も指定されていなかったらmodelとtypeに空文字を保存
      saveCookie(SasCookieKeys.KEY_MODEL_NAME, nowFilter);
      saveCookie(SasCookieKeys.KEY_TYPE, nowTypeFilter);
    }
    // cookieからregionの情報(JPとか)を取得する
    const cookie = loadCookie();
    const region = cookie[SasCookieKeys.KEY_REGION];
    // jsonファイルの情報を取得する
    Promise.all([
      fetch(`${sasStaticUrl}/st/json/regions.json`),
      fetch(`${sasStaticUrl}/st/json/models.json`)
    ])
      .then((responses) => {
        if (!responses[0].ok || !responses[1].ok) {
          throw new Error('not ok');
        }
        return Promise.all(responses.map((response) => response.json()));
      })
      .then(([jsonData1, jsonData2]) => {
        // 現在の地域に対応したリージョンID(0とか)をregions.jsonファイルから取得してセットする
        setRegionCode(jsonData1.region[region]);
        // models.jsonファイルから取得したデータをセットする
        setPIdModelNameList(jsonData2.models);
        // 全pId(allを除く)と'all'をoptionsにセットする
        const pIdModelOptions = Object.keys(jsonData2.models).filter(
          (key) => key !== 'all'
        );
        const initialModelOptions = ['all', ...pIdModelOptions];
        dispatch(setModelOptions(initialModelOptions));

        if (nowFilter && nowTypeFilter) {
          // クエリにmodelとtypeが指定されている場合、modelを優先
          if (initialModelOptions.some((model) => model === nowFilter)) {
            // 機種名がクエリに指定されている場合(allも含む)(=「カメラ有償機能」「〇〇」)
            dispatch(setPlanValue(PlanOptionValue.CAMERA_PAID_FEATURES));
            dispatch(setModelValue(nowFilter));
          } else {
            // 存在しない機種名などがクエリに指定されている場合(=「カメラ有償機能」「すべての機種」)
            dispatch(setPlanValue(PlanOptionValue.CAMERA_PAID_FEATURES));
            dispatch(setModelValue('all'));
          }
        } else if (nowTypeFilter) {
          // クエリにtypeが指定されている場合(app以外も含む)(=「アプリ/サービス」「-」)
          dispatch(setPlanValue(PlanOptionValue.APP_SERVICE));
          dispatch(setModelValue(''));
        } else if (
          nowFilter === PlanOptionValue.ALL_PLAN &&
          nowTypeFilter === PlanOptionValue.ALL_PLAN
        ) {
          // クエリに何も指定されていない場合(=「すべてのプラン」「-」)
          dispatch(setPlanValue(PlanOptionValue.ALL_PLAN));
          dispatch(setModelValue(''));
        } else if (initialModelOptions.some((model) => model === nowFilter)) {
          // クエリがmodelで機種名が指定されている場合(allも含む)(=「カメラ有償機能」「〇〇」)
          dispatch(setPlanValue(PlanOptionValue.CAMERA_PAID_FEATURES));
          dispatch(setModelValue(nowFilter));
        } else {
          // クエリがmodelで存在しない機種名などが指定されている場合(=「カメラ有償機能」「すべての機種」)
          dispatch(setPlanValue(PlanOptionValue.CAMERA_PAID_FEATURES));
          dispatch(setModelValue('all'));
        }
      })
      .catch((error) => console.log('Error fetching data:', error));
  }, []);

  /** 表示用に機種名を変換 */
  const translateModelOption = (model: string) => {
    if (model === 'all') {
      return glossary.glx(PlanListGL.AllModels);
    } else {
      if (Object.prototype.hasOwnProperty.call(pIdModelNameList, model)) {
        // pIdModelNameListの中に一致するpidがある場合
        const innerObj = pIdModelNameList[model];
        if (
          Object.prototype.hasOwnProperty.call(
            innerObj.name_by_region,
            regionCode
          )
        ) {
          // innerObjの中に一致するリージョンIDのデータがある場合、モデル名を返す
          return innerObj.name_by_region[regionCode];
        }
      }
    }
  };

  return (
    <div className="filter">
      <label className="filterSelect" data-test="planSelect">
        <select
          value={planValue}
          onChange={onChangePlan}
          disabled={isProcessingList}
        >
          <option value={PlanOptionValue.ALL_PLAN}>
            {glossary.glx(PlanListGL.AllPlans)}
          </option>
          <option value={PlanOptionValue.CAMERA_PAID_FEATURES}>
            {glossary.glx(PlanListGL.CameraPaidFeatures)}
          </option>
          <option value={PlanOptionValue.APP_SERVICE}>
            {glossary.glx(PlanListGL.App)}
          </option>
        </select>
      </label>
      <label className="filterSelect" data-test="modelSelect">
        <select
          value={modelValue}
          onChange={onChangeModel}
          disabled={
            planValue === PlanOptionValue.ALL_PLAN ||
            planValue === PlanOptionValue.APP_SERVICE ||
            isProcessingList
          }
          className="filterSelect"
        >
          {/* すべてのプランorアプリ/サービスの時は「-」、カメラ有償機能の時は「すべての機種or機種名」を表示 */}
          {planValue === PlanOptionValue.ALL_PLAN ||
          planValue === PlanOptionValue.APP_SERVICE ? (
            <option value="">-</option>
          ) : (
            modelOptions.map((model) => (
              <option key={model} value={model}>
                {translateModelOption(model)}
              </option>
            ))
          )}
        </select>
      </label>
      <button
        className="filterClearButton"
        data-test="filterClearButton"
        onClick={onClickFilterClear}
        disabled={isProcessingList}
      >
        {glossary.glx(PlanListGL.Clear)}
      </button>
    </div>
  );
};
